import { Lines, PageHeading, SkeletonBlock } from './SkeletonPrimitives';
import '../pages/catalogLayout.css';
import '../pages/companies.css';
import '../pages/manufacturerEquipment.css';
import './productImage.css';
import './catalogSkeletons.css';

export const CATALOG_SKELETON_VARIANTS = [
  'catalog',
  'catalog-list',
  'catalog-filters',
  'catalog-editor',
  'product',
  'reviews',
  'companies',
  'companies-list',
  'company',
  'company-equipment',
  'business-reviews',
  'manufacturer-equipment',
  'manufacturer-list',
  'manufacturer-form',
  'manufacturer-detail',
] as const;

function Chips({ count = 3 }: { count?: number }) {
  return (
    <div className="sk-chips">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonBlock key={i} />
      ))}
    </div>
  );
}

function CatalogFilters({ count }: { count: number }) {
  return (
    <div className="filter-panel catalog-filter-panel sk-catalog-filters">
      {Array.from({ length: count }, (_, i) => (
        <div className="sk-catalog-field" key={i}>
          <SkeletonBlock className="sk-line sk-line-short" />
          <SkeletonBlock className="sk-control" />
        </div>
      ))}
    </div>
  );
}

function CatalogEditor() {
  const field = (key: string, textarea = false) => (
    <div className="sk-catalog-field" key={key}>
      <SkeletonBlock className="sk-line sk-line-short" />
      <SkeletonBlock className={textarea ? 'sk-catalog-textarea' : 'sk-control'} />
    </div>
  );
  return (
    <div className="stack-form catalog-product-form sk-catalog-editor">
      {field('name')}
      {field('slug')}
      <div className="form-columns">
        {field('manufacturer')}
        {field('category')}
      </div>
      {field('model')}
      {field('description', true)}
      {field('source')}
      {field('status')}
      <div className="form-columns">
        {field('width')}
        {field('depth')}
        {field('height')}
      </div>
    </div>
  );
}

function CatalogCards({ count, list }: { count: number; list: boolean }) {
  return (
    <div className={`product-grid sk-catalog-grid${list ? ' product-list' : ''}`}>
      {Array.from({ length: count }, (_, i) => (
        <div className="product-card" key={i}>
          <div className="product-image">
            <SkeletonBlock className="sk-catalog-fill" />
          </div>
          <div className="product-card-body">
            <SkeletonBlock className="product-category sk-line sk-catalog-category" />
            <SkeletonBlock className="product-title sk-catalog-product-title" />
            <SkeletonBlock className="product-brand sk-line sk-line-short" />
            <div className="product-muscles">
              <Chips />
            </div>
            <SkeletonBlock className="rating sk-line sk-catalog-rating" />
            <div className="product-card-bottom">
              <SkeletonBlock className="sk-catalog-price" />
              <SkeletonBlock className="sk-catalog-add" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Catalog({ count, list, page }: { count: number; list: boolean; page: boolean }) {
  if (!page) return <CatalogCards count={count} list={list} />;
  return (
    <>
      <PageHeading action />
      <div className="catalog-layout">
        <div className="catalog-tools">
          <SkeletonBlock className="catalog-search sk-catalog-search" />
          <SkeletonBlock className="catalog-filter-toggle sk-catalog-icon-control" />
          <div className="view-toggle sk-catalog-view-toggle">
            <SkeletonBlock />
            <SkeletonBlock />
          </div>
        </div>
        <aside className="catalog-filter-sidebar">
          <div className="catalog-filter-heading">
            <SkeletonBlock className="sk-line" />
            <SkeletonBlock className="sk-catalog-filter-clear" />
          </div>
          <CatalogFilters count={6} />
        </aside>
        <div className="catalog-results">
          <div className="results-row sk-catalog-results-row">
            <SkeletonBlock className="sk-line sk-catalog-count" />
            <SkeletonBlock className="sk-line sk-catalog-sort" />
          </div>
          <CatalogCards count={count} list={list} />
        </div>
      </div>
    </>
  );
}

function CompanyCards({ count, list }: { count: number; list: boolean }) {
  return (
    <>
      <div className="company-result-count sk-catalog-result-count">
        <SkeletonBlock className="sk-line sk-catalog-count" />
      </div>
      <div className={`company-grid${list ? ' company-list' : ''}`}>
        {Array.from({ length: count }, (_, i) => (
          <div className="company-card" key={i}>
            <div className="company-card-top">
              <SkeletonBlock className="company-mark" />
            </div>
            <div className="company-card-content">
              <SkeletonBlock className="sk-catalog-company-title" />
              <div className="sk-catalog-company-description">
                <Lines />
              </div>
              <div className="company-card-categories">
                <SkeletonBlock />
                <SkeletonBlock />
                <SkeletonBlock />
              </div>
            </div>
            <div className="company-card-footer">
              <SkeletonBlock className="sk-line sk-catalog-count" />
              <SkeletonBlock className="sk-catalog-arrow" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Companies({ count, list, page }: { count: number; list: boolean; page: boolean }) {
  const content = <CompanyCards count={count} list={list} />;
  if (!page) return content;
  return (
    <div className="companies-page">
      <PageHeading action />
      <div className="companies-intro">
        <SkeletonBlock className="companies-intro-icon" />
        <div className="sk-catalog-intro-copy">
          <SkeletonBlock className="sk-title" />
          <Lines />
        </div>
      </div>
      <div className="companies-tools">
        <SkeletonBlock className="company-search sk-catalog-search" />
        <div className="company-type-filter sk-catalog-type-filter">
          <SkeletonBlock className="sk-control" />
        </div>
        <div className="view-toggle companies-view-toggle sk-catalog-view-toggle">
          <SkeletonBlock />
          <SkeletonBlock />
        </div>
      </div>
      {content}
    </div>
  );
}

function CompanyEquipment({ count }: { count: number }) {
  return (
    <div className="company-equipment-grid">
      {Array.from({ length: count }, (_, i) => (
        <div className="company-equipment" key={i}>
          <div>
            <SkeletonBlock className="sk-catalog-fill" />
          </div>
          <SkeletonBlock className="sk-line sk-catalog-category" />
          <SkeletonBlock className="sk-catalog-equipment-title" />
        </div>
      ))}
    </div>
  );
}

function CompanyProfile({ count }: { count: number }) {
  return (
    <div className="companies-page company-profile">
      <div className="back-link">
        <SkeletonBlock className="sk-line sk-catalog-back" />
      </div>
      <div className="company-profile-header">
        <SkeletonBlock className="company-mark company-mark-large" />
        <div className="company-profile-title sk-catalog-company-heading">
          <SkeletonBlock className="sk-line sk-line-short" />
          <SkeletonBlock className="sk-title" />
          <Chips count={2} />
        </div>
      </div>
      <div className="company-profile-columns">
        <div className="company-profile-main">
          <div className="company-section">
            <div className="company-section-heading">
              <SkeletonBlock className="sk-title" />
            </div>
            <Lines />
            <div className="sk-catalog-paragraph">
              <Lines />
            </div>
            <SkeletonBlock className="sk-line sk-line-short" />
          </div>
          <div className="company-section">
            <div className="company-section-heading">
              <SkeletonBlock className="sk-title" />
            </div>
            <div className="company-categories">
              {Array.from({ length: 4 }, (_, i) => (
                <SkeletonBlock className="sk-control sk-catalog-category-link" key={i} />
              ))}
            </div>
            <CompanyEquipment count={Math.min(count, 3)} />
          </div>
        </div>
        <div className="company-profile-side">
          {Array.from({ length: 3 }, (_, i) => (
            <div className="company-section" key={i}>
              <SkeletonBlock className="sk-catalog-side-icon" />
              <div className="sk-catalog-paragraph">
                <SkeletonBlock className="sk-title" />
              </div>
              <Lines />
              <div className="sk-catalog-paragraph">
                <SkeletonBlock className="sk-control" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Reviews({ count, business = false }: { count: number; business?: boolean }) {
  if (business)
    return (
      <div className="sk-catalog-business-reviews">
        <div className="company-rating-summary">
          <SkeletonBlock className="sk-catalog-score" />
          <Lines short />
        </div>
        {Array.from({ length: count }, (_, i) => (
          <div className="company-business-review" key={i}>
            <div>
              <SkeletonBlock className="sk-line sk-catalog-review-author" />
              <SkeletonBlock className="sk-line sk-catalog-stars" />
            </div>
            <div className="sk-catalog-paragraph">
              <Lines />
            </div>
            <footer>
              <SkeletonBlock className="sk-line sk-catalog-date" />
              <SkeletonBlock className="sk-line sk-catalog-stars" />
            </footer>
          </div>
        ))}
      </div>
    );
  return (
    <div className="sk-catalog-reviews">
      {Array.from({ length: count }, (_, i) => (
        <div className="review" key={i}>
          <div className="row">
            <SkeletonBlock className="sk-avatar sk-catalog-review-avatar" />
            <div className="sk-catalog-review-person">
              <Lines short />
            </div>
            <SkeletonBlock className="sk-line sk-catalog-stars" />
          </div>
          <SkeletonBlock className="sk-catalog-review-title" />
          <Lines />
          <div className="sk-catalog-paragraph">
            <SkeletonBlock className="sk-line sk-line-short" />
          </div>
          <div className="review-actions">
            <SkeletonBlock className="sk-line sk-catalog-stars" />
            <SkeletonBlock className="sk-line sk-catalog-stars" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Product() {
  return (
    <>
      <div className="back-link">
        <SkeletonBlock className="sk-line sk-catalog-back" />
      </div>
      <div className="product-detail">
        <div className="product-detail-art">
          <SkeletonBlock className="sk-catalog-fill" />
        </div>
        <div className="product-detail-info sk-catalog-product-info">
          <SkeletonBlock className="sk-line sk-line-short" />
          <SkeletonBlock className="sk-catalog-detail-title" />
          <SkeletonBlock className="sk-line sk-catalog-stars" />
          <Lines />
          <Lines />
          <Chips />
          <div className="sk-catalog-detail-price">
            <SkeletonBlock className="sk-catalog-price" />
            <Lines short />
          </div>
          <SkeletonBlock className="sk-control" />
          <SkeletonBlock className="sk-catalog-notice" />
        </div>
      </div>
      <div className="detail-columns">
        <div className="panel">
          <div className="panel-heading">
            <SkeletonBlock className="sk-title" />
          </div>
          <div className="sk-catalog-specs">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i}>
                <SkeletonBlock className="sk-line" />
                <SkeletonBlock className="sk-line" />
              </div>
            ))}
          </div>
        </div>
        <div className="panel">
          <div className="panel-heading">
            <SkeletonBlock className="sk-title" />
          </div>
          <div className="rating-summary">
            <SkeletonBlock className="sk-catalog-score" />
            <Lines short />
          </div>
          <Reviews count={2} />
        </div>
      </div>
    </>
  );
}

function ManufacturerList({ count }: { count: number }) {
  return (
    <div className="sk-catalog-manufacturer-list">
      <SkeletonBlock className="sk-line sk-catalog-count" />
      <div className="manufacturer-equipment-list">
        {Array.from({ length: count }, (_, i) => (
          <div className="sk-catalog-manufacturer-row" key={i}>
            <Lines short />
            <SkeletonBlock className="sk-catalog-status" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ManufacturerForm({ count }: { count: number }) {
  return (
    <div className="manufacturer-editor">
      <div className="manufacturer-editor-heading">
        <div className="sk-catalog-intro-copy">
          <SkeletonBlock className="sk-line sk-line-short" />
          <SkeletonBlock className="sk-title" />
          <SkeletonBlock className="sk-line" />
        </div>
      </div>
      <div className="manufacturer-form-section">
        <SkeletonBlock className="sk-title" />
        <Lines />
        <div className="manufacturer-form-columns">
          {Array.from({ length: count }, (_, i) => (
            <div className="sk-catalog-field" key={i}>
              <SkeletonBlock className="sk-line sk-line-short" />
              <SkeletonBlock className="sk-control" />
            </div>
          ))}
        </div>
        <div className="sk-catalog-field">
          <SkeletonBlock className="sk-line sk-line-short" />
          <SkeletonBlock className="sk-catalog-textarea" />
        </div>
      </div>
      <div className="manufacturer-form-section">
        <SkeletonBlock className="sk-title" />
        <div className="manufacturer-three-columns">
          {Array.from({ length: 3 }, (_, i) => (
            <div className="sk-catalog-field" key={i}>
              <SkeletonBlock className="sk-line sk-line-short" />
              <SkeletonBlock className="sk-control" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Manufacturer({ count }: { count: number }) {
  return (
    <div className="manufacturer-equipment-page">
      <div className="back-link">
        <SkeletonBlock className="sk-line sk-catalog-back" />
      </div>
      <PageHeading />
      <SkeletonBlock className="sk-catalog-access" />
      <div className="manufacturer-workspace">
        <div className="manufacturer-list-panel">
          <SkeletonBlock className="sk-control sk-catalog-full-control" />
          <div className="manufacturer-status-filter sk-catalog-field">
            <SkeletonBlock className="sk-line sk-line-short" />
            <SkeletonBlock className="sk-control" />
          </div>
          <ManufacturerList count={count} />
        </div>
        <div className="manufacturer-editor-panel">
          <ManufacturerForm count={6} />
        </div>
      </div>
    </div>
  );
}

/** Decorative content only; Skeleton supplies the single live announcement. */
export function CatalogSkeleton({
  variant,
  count,
  page = false,
}: {
  variant: string;
  count: number;
  page?: boolean;
}) {
  switch (variant) {
    case 'catalog':
    case 'catalog-list':
      return <Catalog count={count} list={variant === 'catalog-list'} page={page} />;
    case 'catalog-filters':
      return <CatalogFilters count={count} />;
    case 'catalog-editor':
      return <CatalogEditor />;
    case 'companies':
    case 'companies-list':
      return <Companies count={count} list={variant === 'companies-list'} page={page} />;
    case 'company':
      return <CompanyProfile count={count} />;
    case 'company-equipment':
      return <CompanyEquipment count={count} />;
    case 'product':
      return <Product />;
    case 'reviews':
      return <Reviews count={count} />;
    case 'business-reviews':
      return <Reviews count={count} business />;
    case 'manufacturer-equipment':
      return <Manufacturer count={count} />;
    case 'manufacturer-list':
      return <ManufacturerList count={count} />;
    case 'manufacturer-form':
    case 'manufacturer-detail':
      return <ManufacturerForm count={count} />;
    default:
      return null;
  }
}
